import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ChevronLeft, KeyRound } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { membershipService } from '../services/dataService';
import { notify } from '../utils/notifications';

export default function JoinWithCodeScreen() {
  const navigate = useNavigate();
  const {
    setCurrentEventId,
    refreshMembership,
    authSession,
    isAuthSessionCurrent,
  } = useApp();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  async function handleJoin() {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) { notify.error('Please enter an invite code'); return; }
    const session = authSession;
    if (!session || !isAuthSessionCurrent(session)) return;

    setIsLoading(true);
    try {
      const result = await membershipService.joinWithCode(
        trimmed,
        { signal: session.signal },
      );
      if (!isAuthSessionCurrent(session)) return;
      if (result.already_joined) {
        notify.info('Already joined', "You're already a member of this conference.");
      } else {
        notify.success('Joined!', 'Welcome to the conference.');
      }
      setCurrentEventId(result.event_id);
      localStorage.setItem('current-event-id', result.event_id);
      if (!isAuthSessionCurrent(session)) return;
      await refreshMembership(session);
      if (!isAuthSessionCurrent(session)) return;
      navigate('/event-interests', { replace: true });
    } catch (err: unknown) {
      const e = err as { reason?: string; message?: string };
      const msgs: Record<string, string> = {
        invalid_code: 'That code is not valid. Please check and try again.',
        revoked: 'That invite code has been revoked.',
        expired: 'That invite code has expired.',
        exhausted: 'That invite code has reached its usage limit.',
        verify_email: 'Please verify your email before joining.',
        profile_required: 'Please complete your profile before joining.',
      };
      if (isAuthSessionCurrent(session)) {
        notify.error('Could not join', msgs[e.reason ?? ''] ?? (e.message ?? 'Please try again.'));
      }
    } finally {
      if (isAuthSessionCurrent(session)) setIsLoading(false);
    }
  }

  return (
    <div
      className="app-screen-header size-full flex flex-col px-6 pt-14"
      style={{ backgroundColor: 'var(--app-bg)' }}
    >
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 mb-8 active:opacity-70 transition-opacity"
        style={{ color: 'var(--app-text-muted)' }}
      >
        <ChevronLeft size={18} />
        <span className="text-sm">Back</span>
      </button>

      {/* Icon + heading */}
      <div className="flex flex-col items-center mb-10">
        <div
          className="rounded-2xl size-16 flex items-center justify-center mb-6 border"
          style={{ backgroundColor: 'var(--app-accent-dim)', borderColor: 'var(--app-accent-border)' }}
        >
          <KeyRound className="w-8 h-8" style={{ color: 'var(--app-accent)' }} />
        </div>
        <h1 className="text-[28px] font-semibold tracking-tight text-center" style={{ color: 'var(--app-text)' }}>
          Join a Conference
        </h1>
        <p className="text-base text-center mt-2" style={{ color: 'var(--app-text-muted)' }}>
          Enter the invite code you received from the conference organizer.
        </p>
      </div>

      {/* Code input */}
      <div className="flex flex-col gap-2 mb-6">
        <label className="text-base" style={{ color: 'var(--app-text)' }}>Invite Code</label>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
          placeholder="e.g. NEURIPS26-STUDENT"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          className="rounded-lg px-4 text-base font-mono outline-none transition-colors w-full tracking-widest"
          style={{
            backgroundColor: 'var(--app-input-bg)',
            border: '1px solid var(--app-input-border)',
            color: 'var(--app-text)',
            height: '56px',
            letterSpacing: '0.1em',
          }}
        />
      </div>

      {/* Join button */}
      <button
        onClick={handleJoin}
        disabled={isLoading || !code.trim()}
        className="h-14 rounded-xl text-white text-lg w-full flex items-center justify-center active:scale-95 transition-transform disabled:opacity-60"
        style={{
          backgroundColor: 'var(--app-accent-solid)',
          boxShadow: '0 10px 20px var(--app-accent-glow)',
        }}
      >
        {isLoading ? (
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          'Join Conference'
        )}
      </button>
    </div>
  );
}
